// WebAuthn passkey helpers — base64url + browser API wrappers.
import api from "@/lib/api";

const b64urlToBytes = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

const bytesToB64url = (buf) => {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const isWebAuthnSupported = () =>
  typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials?.create;

// --- Registration (logged-in user) ---
export async function registerPasskey(friendlyName = "Passkey") {
  if (!isWebAuthnSupported()) throw new Error("This browser doesn't support passkeys");

  const { data: opts } = await api.post("/auth/passkey/register/options");

  // Re-hydrate base64url fields into ArrayBuffer
  const publicKey = {
    ...opts,
    challenge: b64urlToBytes(opts.challenge),
    user: { ...opts.user, id: b64urlToBytes(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64urlToBytes(c.id),
    })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("Passkey creation cancelled");

  const payload = {
    name: friendlyName,
    credential: {
      id: cred.id,
      rawId: bytesToB64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bytesToB64url(cred.response.clientDataJSON),
        attestationObject: bytesToB64url(cred.response.attestationObject),
        transports: cred.response.getTransports ? cred.response.getTransports() : [],
      },
      clientExtensionResults: cred.getClientExtensionResults?.() || {},
    },
  };
  const { data } = await api.post("/auth/passkey/register/verify", payload);
  return data;
}

// --- Authentication (passwordless) ---
export async function signInWithPasskey(email) {
  if (!isWebAuthnSupported()) throw new Error("This browser doesn't support passkeys");

  const { data: opts } = await api.post("/auth/passkey/login/options", { email });
  const publicKey = {
    ...opts,
    challenge: b64urlToBytes(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64urlToBytes(c.id),
    })),
  };

  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error("Passkey sign-in cancelled");

  const payload = {
    credential: {
      id: cred.id,
      rawId: bytesToB64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bytesToB64url(cred.response.clientDataJSON),
        authenticatorData: bytesToB64url(cred.response.authenticatorData),
        signature: bytesToB64url(cred.response.signature),
        userHandle: cred.response.userHandle ? bytesToB64url(cred.response.userHandle) : null,
      },
      clientExtensionResults: cred.getClientExtensionResults?.() || {},
    },
  };
  const { data } = await api.post("/auth/passkey/login/verify", payload);
  return data;
}

export async function listMyPasskeys() {
  const { data } = await api.get("/auth/passkey");
  return data?.items || [];
}

export async function deleteMyPasskey(id) {
  await api.delete(`/auth/passkey/${id}`);
}

export async function checkHasPasskey(email) {
  try {
    const { data } = await api.post("/auth/passkey/has-passkey", { email });
    return !!data?.has_passkey;
  } catch {
    return false;
  }
}

export async function adminResetUserPasskeys(userId) {
  const { data } = await api.post(`/auth/passkey/admin-reset/${userId}`);
  return data;
}
